"use client";
import { useState } from "react";
import Link from "next/link";
import { useFirstInteraction } from "@/lib/useFirstInteraction";
import { gtag, pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";
import { isMaintenanceActive } from "@/components/maintenance/flag";

const STORAGE_KEY = "sticko_consent";

type Choice = "granted" | "denied";

function readStored(): Choice | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function ConsentBanner() {
  const ready = useFirstInteraction();
  const [dismissed, setDismissed] = useState(() => readStored() !== null);

  function decide(choice: Choice) {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // ignore quota errors
    }
    gtag("consent", "update", {
      ad_storage: choice,
      ad_user_data: choice,
      ad_personalization: choice,
      analytics_storage: choice,
    });
    pushEvent(analyticsEvents.consent_choice, { choice });
    setDismissed(true);
  }

  // isMaintenanceActive() reads the DOM, so it must never run during
  // hydration's first render. It cannot: `ready` comes from
  // useFirstInteraction() and is false on the first client render in every
  // case, so `||` short-circuits before it is evaluated. Keep it last.
  if (!ready || dismissed || isMaintenanceActive()) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="border-border fixed inset-x-4 bottom-4 z-30 rounded-xl border bg-white p-4 shadow-(--shadow-card) md:right-auto md:bottom-6 md:left-6 md:max-w-sm"
    >
      <p className="text-foreground text-[13px] leading-snug">
        We use cookies for analytics and personalised ads. You can change this any time on our{" "}
        <Link href="/cookie-policy" className="underline">
          cookie policy
        </Link>{" "}
        page.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => decide("granted")}
          className="rounded-pill bg-brand-500 hover:bg-brand-600 flex-1 px-4 py-1.5 text-[13px] font-semibold text-white"
        >
          Accept all
        </button>
        <button
          type="button"
          onClick={() => decide("denied")}
          className="rounded-pill border-border bg-muted text-muted-foreground hover:bg-muted/80 flex-1 border px-4 py-1.5 text-[13px] font-semibold"
        >
          Reject
        </button>
      </div>
    </div>
  );
}
